export const prefixZeroFormat = (n) => {
  return n > 9 ? '' + n : '0' + n;
};

export const round = (n, dp = 2) => {
  if (typeof n === 'string' || n instanceof String) {
    return Number(parseFloat(n)?.toFixed?.(dp));
  }
  return Number(n?.toFixed?.(dp));
};

/**
 * Converts a value from a given unit to meters.
 * Handles common units like meters, centimeters, millimeters, inches, and feet.
 *
 * @param {number | string | undefined | null} value - The numeric value to convert.
 * @param {string} [unit='m'] - The unit of the input value (e.g., 'cm', 'm', 'mm', 'in', 'ft'). Defaults to 'm'.
 * @returns {number} - The value converted to meters. Returns 0 if the input value is invalid or zero.
 */
export function toMeter(value, unit = 'm') {
  const numericValue = value && value?.trim?.() !== '' ? parseFloat(value) : 0.0;
  switch (unit?.toLowerCase()) {
    case 'mm':
      return numericValue / 1000;
    case 'cm':
      return numericValue / 100;
    case 'km':
      return numericValue * 1000;
    case 'm':
      return numericValue;
    default:
      console.warn(`Warning! : Unknown Unit '${unit}' at Unit Converter, Value returned as is.`);
      return numericValue;
  }
}

export function toCM(value, unit = 'cm') {
  const numericValue = value && value?.trim?.() !== '' ? parseFloat(value) : 0.0;
  switch (unit?.toLowerCase()) {
    case 'mm':
      return numericValue / 10;
    case 'm':
      return numericValue * 100;
    case 'km':
      return numericValue * 100000;
    case 'cm':
      return numericValue;
    default:
      console.warn(`Warning! : Unknown Unit '${unit}' at Unit Converter, Value returned as is.`);
      return numericValue;
  }
}

export function toKG(value, unit) {
  const numericValue = value && value?.trim?.() !== '' ? parseFloat(value) : 0.0;
  switch (unit?.toLowerCase()) {
    case 'g':
      return numericValue / 1000;
    case 'mg':
      return numericValue / 1000000;
    case 't':
      return numericValue * 1000;
    case 'kg':
      return numericValue;
    default:
      console.warn(`Warning! : Unknown Unit '${unit}' at Unit Converter, Value returned as is.`);
      return numericValue;
  }
}

/**
 * Calculates load meters based on item dimensions and quantity.
 * This uses the common method of calculating the total floor area required
 * by the items and dividing it by the standard width of a truck trailer.
 * Height is not typically used in this specific calculation method.
 *
 * @param {object} options - An object containing the calculation parameters.
 * @param {number | string} options.itemWidth - Width of a single item.
 * @param {number | string} options.itemLength - Length (depth) of a single item.
 * @param {number | string} options.quantity - The total number of items.
 * @param {string} [options.widthUnit='m'] - The unit for itemWidth ('m', 'cm', 'mm', 'in', 'ft'). Defaults to 'm'.
 * @param {string} [options?.lengthUnit='m'] - The unit for itemLength ('m', 'cm', 'mm', 'in', 'ft'). Defaults to 'm'.
 * @param {number} [options.standardTruckWidth=2.4] - The standard width of the truck trailer in meters used for the calculation. Defaults to 2.4 meters.
 * @returns {number} - Calculated load meters. Returns 0 if quantity is zero, or if width/length results in zero meters, or if standardTruckWidth is invalid.
 */
export const calculateLoadMeters = ({
  itemWidth,
  itemLength,
  quantity,
  widthUnit = 'm',
  lengthUnit = 'm',
  standardTruckWidth = 2.4, // Common standard width in meters
}) => {
  // Ensure quantity is a valid, non-negative number. Default to 0 if invalid.
  const numQuantity = Math.max(0, Number(quantity) || 0);

  // Ensure standardTruckWidth is a valid, positive number. Use default if invalid.
  const numTruckWidth = Number(standardTruckWidth);
  const validTruckWidth = numTruckWidth > 0 ? numTruckWidth : 2.4;

  // If there are no items, load meters are 0.
  if (numQuantity === 0) {
    return 0;
  }

  // Convert item dimensions to meters using the helper function.
  const widthM = toMeter(itemWidth, widthUnit);
  const lengthM = toMeter(itemLength, lengthUnit);

  // If either dimension converts to 0 meters, the area is 0, so load meters are 0.
  if (widthM <= 0 || lengthM <= 0) {
    return 0;
  }

  // Calculate the total floor area needed for all items in square meters.
  const totalFloorArea = widthM * lengthM * numQuantity;

  // Calculate load meters by dividing the total area by the standard truck width.
  const loadMeters = totalFloorArea / validTruckWidth;

  // Return the calculated load meters.
  return loadMeters;
};
